import { FC } from "react";
import { Link, Tooltip, TooltipProps, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ResourceType } from "@/services/cloudchipr.api";
import { useAppSelector } from "@/store/hooks";
import { liveUsageResourceTypeFailedStatusesByResourceTypeSelector } from "@/store/live-usage-mgmt/selectors/resource-info-status/liveUsageResourceTypeFailedStatusesByResourceTypeSelector";
import { liveUsageResourceTypeFailedForAllAccountsSelector } from "@/store/live-usage-mgmt/selectors/resource-info-status/liveUsageResourceTypeFailedForAllAccountsSelector";
import { getResourceTypeName } from "@/utils/helpers/resources/getResourceTypeName";

interface LiveUsageResourceInfoStatusForMultiAccountProps {
  resourceType: ResourceType;
}

export const LiveUsageResourceInfoStatusForMultiAccount: FC<
  LiveUsageResourceInfoStatusForMultiAccountProps
> = ({ resourceType }) => {
  const statuses = useAppSelector(
    liveUsageResourceTypeFailedStatusesByResourceTypeSelector,
  );
  const resourceTypeFailureAccounts = statuses?.[resourceType];
  const isAllMissing = useAppSelector((state) =>
    liveUsageResourceTypeFailedForAllAccountsSelector(state, resourceType),
  );

  if (!resourceTypeFailureAccounts?.length || isAllMissing) {
    return null;
  }

  const accountNamesString = resourceTypeFailureAccounts
    .map(
      ({ provider_account_name, provider_account_id }) =>
        `"${provider_account_name || provider_account_id}"`,
    )
    .join(", ");

  return (
    <Tooltip
      arrow
      placement="bottom"
      componentsProps={tooltipComponentsProps}
      title={
        <Typography fontWeight="medium" variant="caption">
          We couldn’t collect your{" "}
          {getResourceTypeName(resourceType, { type: "long" })} from the{" "}
          <Typography fontWeight="bold" variant="caption">
            {accountNamesString}{" "}
          </Typography>
          account{resourceTypeFailureAccounts.length > 1 ? "s" : ""} during the
          last attempt. <br />
          Please check for missing permissions or reach out to us at{" "}
          <Link
            href="mailto:support@cloudchipr.com"
            underline="hover"
            target="_blank"
          >
            support@cloudchipr.com
          </Link>{" "}
          for help.
        </Typography>
      }
    >
      <WarningAmberIcon color="info" fontSize="small" />
    </Tooltip>
  );
};

const tooltipComponentsProps: TooltipProps["componentsProps"] = {
  tooltip: {
    sx: {
      p: 1,
      border: 1,
      boxShadow: 7,
      maxWidth: 600,
      maxHeight: 300,
      borderRadius: 3,
      bgcolor: "white",
      overflow: "auto",
      color: "text.primary",
      borderColor: "grey.300",
      "& .MuiTooltip-arrow": { color: "background.paper" },
    },
  },
};
